import { useLayoutEffect, useRef } from 'react';

import { focusFirstAvailable } from 'core/utils/focusTargets';

import type { FocusTarget } from 'core/utils/focusTargets';

export type FocusAfterRemovalRequest = {
  getTargets: () => ReadonlyArray<FocusTarget>;
  hasBeenRemoved: () => boolean;
};

type UseFocusAfterRemoval = {
  keepFocusAfterRemoval: (request: FocusAfterRemovalRequest) => void;
};

/**
 * Lleva el foco a un destino contiguo cuando se borra el elemento que lo tenía (o el diálogo que lo
 * borró) y el foco cae en `body`. Se pide antes de borrar; los destinos se calculan después, en el DOM ya actualizado.
 */
function useFocusAfterRemoval(): UseFocusAfterRemoval {
  const requestRef = useRef<FocusAfterRemovalRequest | null>(null);

  function keepFocusAfterRemoval(request: FocusAfterRemovalRequest): void {
    requestRef.current = request;
  }

  // Sin deps: el borrado llega tras una petición en un render que no se puede prever, y sin petición solo se lee un ref
  useLayoutEffect(() => {
    const request = requestRef.current;
    if (!request?.hasBeenRemoved()) return;

    requestRef.current = null;
    const { activeElement } = document;
    if (activeElement && activeElement !== document.body) return;

    focusFirstAvailable(request.getTargets());
  });

  return { keepFocusAfterRemoval };
}

export default useFocusAfterRemoval;
